type MusicTimer = ReturnType<typeof window.setInterval>;
type AudioSampleKey = 'ottoBark' | 'ottoPantShort' | 'ottoPant' | 'scoreTable' | 'menuBg' | 'tickWarning' | 'tickBattle';

type AudioSampleOptions = {
  loop?: boolean;
  playbackRate?: number;
  restart?: boolean;
  volume?: number;
};

const AUDIO_SAMPLE_PATHS: Record<AudioSampleKey, string> = {
  ottoBark: '/game-assets/audio/video-project-ladrido.m4a',
  ottoPantShort: '/game-assets/audio/hadeo-corto.mp3',
  ottoPant: '/game-assets/audio/hadeo.mp3',
  scoreTable: '/game-assets/audio/tabla-de-puntuacion.mp3',
  menuBg: '/game-assets/audio/inicio-de-juego-arcade.mp3',
  tickWarning: '/game-assets/audio/mushroom-warning.mp3',
  tickBattle: '/game-assets/audio/tick-160bpm.mp3',
};

export class SoundSystem {
  private context: AudioContext | null = null;
  private enabled = true;
  private userGestureSeen = false;
  private readonly activeSamples = new Set<HTMLAudioElement>();
  private readonly loopingSamples = new Map<AudioSampleKey, HTMLAudioElement>();
  private gameplayMusicTimer?: MusicTimer;
  private finalSuspenseTimer?: MusicTimer;
  private scoreMusicTimer?: MusicTimer;
  private lossMusicTimer?: MusicTimer;
  private gameplayStep = 0;
  private suspenseStep = 0;
  private scoreStep = 0;
  private lossStep = 0;
  private scoreMusicIsLeader = false;

  setEnabled(enabled: boolean) {
    this.enabled = enabled;

    if (!enabled) {
      this.stopAllLoops();
    }
  }

  unlock() {
    this.userGestureSeen = true;

    if (!this.enabled) {
      return Promise.resolve();
    }

    const audioContext = this.getContext();

    if (!audioContext) {
      return Promise.resolve();
    }

    const playPrimer = () => {
      const source = audioContext.createBufferSource();
      source.buffer = audioContext.createBuffer(1, 1, audioContext.sampleRate);
      source.connect(audioContext.destination);
      source.start();
    };

    if (audioContext.state === 'suspended') {
      return audioContext
        .resume()
        .then(playPrimer)
        .catch(() => undefined);
    }

    playPrimer();
    return Promise.resolve();
  }

  canPlayMedia() {
    return this.enabled && this.userGestureSeen;
  }

  startMenuMusic() {
    if (!this.enabled) {
      return;
    }

    this.stopGameplayMusic();
    this.stopFinalSuspense();
    this.stopScoreMusic();
    this.stopLossMusic();
    this.stopLoopingSample('scoreTable');

    const currentMenu = this.loopingSamples.get('menuBg');

    if (currentMenu && !currentMenu.paused) {
      return;
    }

    if (currentMenu) {
      this.stopLoopingSample('menuBg');
    }

    void this.playSample('menuBg', { loop: true, volume: 0.64 });
  }

  stopMenuMusic() {
    this.stopLoopingSample('menuBg');
  }

  startGameplayMusic() {
    if (!this.enabled) {
      return;
    }

    this.stopMenuMusic();
    this.stopScoreMusic();
    this.stopLossMusic();

    if (this.gameplayMusicTimer) {
      return;
    }

    this.gameplayStep = 0;
    this.playGameplayStep();
    this.gameplayMusicTimer = window.setInterval(() => this.playGameplayStep(), 150);
  }

  stopGameplayMusic() {
    if (!this.gameplayMusicTimer) {
      return;
    }

    window.clearInterval(this.gameplayMusicTimer);
    this.gameplayMusicTimer = undefined;
  }

  startFinalSuspense() {
    if (!this.enabled || this.finalSuspenseTimer) {
      return;
    }

    this.suspenseStep = 0;
    this.playFinalCountdownCue();
    this.finalSuspenseTimer = window.setInterval(() => this.playSuspenseStep(), 420);
  }

  stopFinalSuspense() {
    if (!this.finalSuspenseTimer) {
      return;
    }

    window.clearInterval(this.finalSuspenseTimer);
    this.finalSuspenseTimer = undefined;
  }

  startScoreMusic(isLeader = false) {
    if (!this.enabled) {
      return;
    }

    this.stopMenuMusic();
    this.stopGameplayMusic();
    this.stopFinalSuspense();
    this.stopLossMusic();

    if (this.scoreMusicTimer && this.scoreMusicIsLeader === isLeader) {
      return;
    }

    this.stopScoreMusic();
    this.scoreMusicIsLeader = isLeader;
    this.scoreStep = 0;
    this.playScoreMusicStep();
    this.scoreMusicTimer = window.setInterval(() => this.playScoreMusicStep(), isLeader ? 260 : 430);
  }

  stopScoreMusic() {
    if (!this.scoreMusicTimer) {
      return;
    }

    window.clearInterval(this.scoreMusicTimer);
    this.scoreMusicTimer = undefined;
  }

  startLossMusic() {
    if (!this.enabled || this.lossMusicTimer) {
      return;
    }

    this.stopMenuMusic();
    this.stopGameplayMusic();
    this.stopFinalSuspense();
    this.stopScoreMusic();
    this.lossStep = 0;
    this.playLossMusicStep();
    this.lossMusicTimer = window.setInterval(() => this.playLossMusicStep(), 780);
  }

  stopLossMusic() {
    if (!this.lossMusicTimer) {
      return;
    }

    window.clearInterval(this.lossMusicTimer);
    this.lossMusicTimer = undefined;
  }

  stopAllLoops() {
    this.stopMenuMusic();
    this.stopGameplayMusic();
    this.stopFinalSuspense();
    this.stopScoreMusic();
    this.stopLossMusic();
    this.stopTickBattleMusic();
    this.stopLoopingSamples();
    this.stopActiveSamples();
  }

  playTap() {
    this.playTone(620, 0.08, 'triangle', 0.075);
    this.playTone(980, 0.06, 'sine', 0.034, 0.025);
  }

  playDogClick() {
    this.playToneSweep(420, 820, 0.11, 'triangle', 0.085);
    this.playToneSweep(640, 1040, 0.12, 'sine', 0.058, 0.055);
    this.playNoiseBurst(0.07, 0.036, 1500, 'bandpass', 0.02);
  }

  playDogBark(volume = 0.95) {
    return this.playSample('ottoBark', {
      playbackRate: 0.96 + Math.random() * 0.12,
      volume,
    });
  }

  playOttoTouchReaction() {
    const useShortPant = Math.random() < 0.72;

    void this.playSample(useShortPant ? 'ottoPantShort' : 'ottoPant', {
      playbackRate: useShortPant ? 0.98 + Math.random() * 0.08 : 0.94 + Math.random() * 0.08,
      volume: useShortPant ? 0.92 : 0.72,
    });

    window.setTimeout(() => {
      void this.playDogBark(0.72);
    }, 150);

    return useShortPant ? 980 : 2300;
  }

  startScoreTableMusic() {
    if (!this.enabled) {
      return;
    }

    this.stopMenuMusic();
    this.stopGameplayMusic();
    this.stopFinalSuspense();
    this.stopScoreMusic();
    this.stopLossMusic();
    this.stopLoopingSample('menuBg');
    void this.playSample('scoreTable', { loop: true, volume: 0.78 });
  }

  stopScoreTableMusic() {
    this.stopLoopingSample('scoreTable');
  }

  playTickBossWarning() {
    return this.playSample('tickWarning', { volume: 0.95 });
  }

  startTickBattleMusic() {
    if (!this.enabled) {
      return;
    }

    this.stopMenuMusic();
    this.stopGameplayMusic();
    this.stopFinalSuspense();
    this.stopScoreMusic();
    this.stopLossMusic();

    const currentBattle = this.loopingSamples.get('tickBattle');

    if (currentBattle && !currentBattle.paused) {
      return;
    }

    if (currentBattle) {
      this.stopLoopingSample('tickBattle');
    }

    void this.playSample('tickBattle', { loop: true, volume: 0.86 });
  }

  stopTickBattleMusic() {
    this.stopLoopingSample('tickBattle');
  }

  isTickBattleMusicActive() {
    const currentBattle = this.loopingSamples.get('tickBattle');

    return Boolean(currentBattle && !currentBattle.paused);
  }

  playIntroJingle() {
    [523.25, 659.25, 783.99, 1046.5].forEach((frequency, index) => {
      this.playTone(frequency, 0.18, index % 2 === 0 ? 'triangle' : 'square', 0.07, index * 0.08);
      this.playTone(frequency * 1.5, 0.1, 'sine', 0.025, index * 0.08 + 0.025);
    });
    this.playTone(1318.51, 0.34, 'triangle', 0.06, 0.36);
    this.playNoiseBurst(0.18, 0.034, 3400, 'highpass', 0.34);
  }

  playGameStart() {
    [392, 523.25, 659.25, 783.99, 1046.5].forEach((frequency, index) => {
      this.playTone(frequency, 0.14, 'square', 0.066, index * 0.055);
      this.playTone(frequency * 2, 0.09, 'triangle', 0.028, index * 0.055 + 0.018);
    });
    this.playNoiseBurst(0.18, 0.055, 2600, 'highpass', 0.22);
  }

  playMenuStinger() {
    [587.33, 783.99, 987.77].forEach((frequency, index) => {
      this.playTone(frequency, 0.14, 'triangle', 0.055, index * 0.055);
    });
    this.playNoiseBurst(0.09, 0.018, 3600, 'highpass', 0.12);
  }

  playProductReveal() {
    this.playTone(392, 0.18, 'triangle', 0.05);
    this.playTone(587.33, 0.2, 'triangle', 0.05, 0.08);
    this.playTone(783.99, 0.28, 'sine', 0.052, 0.18);
    this.playNoiseBurst(0.14, 0.022, 2800, 'highpass', 0.18);
  }

  playInstructionOpen() {
    [440, 554.37, 659.25, 880].forEach((frequency, index) => {
      this.playTone(frequency, 0.1, 'triangle', 0.045, index * 0.05);
    });
  }

  playRegisterOpen() {
    this.playToneSweep(480, 900, 0.22, 'triangle', 0.055);
    this.playTone(1200, 0.12, 'sine', 0.03, 0.16);
  }

  playRankingOpen() {
    [523.25, 659.25, 783.99, 1046.5].forEach((frequency, index) => {
      this.playTone(frequency, 0.13, 'square', 0.05, index * 0.06);
    });
    this.playNoiseBurst(0.16, 0.028, 4200, 'highpass', 0.18);
  }

  playReward() {
    this.playTone(740, 0.14, 'square', 0.045);
    this.playTone(920, 0.18, 'triangle', 0.028, 0.05);
  }

  playPenalty() {
    this.playTone(220, 0.12, 'sawtooth', 0.05);
    this.playTone(150, 0.2, 'triangle', 0.04, 0.08);
  }

  playFrenzy() {
    const notes = [660, 820, 990, 1180, 1320];

    notes.forEach((frequency, index) => {
      this.playTone(frequency, 0.12, index % 2 === 0 ? 'square' : 'triangle', 0.04, index * 0.045);
    });
  }

  playFrenzyExtend() {
    this.playTone(1180, 0.08, 'triangle', 0.03);
    this.playTone(1450, 0.1, 'square', 0.026, 0.04);
  }

  playTickAppear() {
    this.playTone(116, 0.18, 'sawtooth', 0.045);
    this.playToneSweep(260, 150, 0.28, 'triangle', 0.026, 0.04);
    this.playNoiseBurst(0.18, 0.025, 680, 'lowpass', 0.04);
  }

  playTickHit() {
    this.playTone(180, 0.11, 'sawtooth', 0.07);
    this.playTone(330, 0.09, 'square', 0.045, 0.04);
    this.playToneSweep(760, 1180, 0.2, 'sawtooth', 0.045, 0.02);
    this.playNoiseBurst(0.11, 0.05, 1800, 'bandpass', 0.05);
  }

  playTickBurst() {
    this.playTone(82, 0.24, 'sine', 0.11);
    this.playTone(136, 0.18, 'sawtooth', 0.075, 0.03);
    this.playTone(420, 0.1, 'square', 0.06, 0.1);
    this.playToneSweep(1180, 230, 0.36, 'sawtooth', 0.07, 0.04);
    this.playNoiseBurst(0.2, 0.12, 520, 'lowpass', 0.04);
    this.playNoiseBurst(0.14, 0.08, 1800, 'bandpass', 0.13);
  }

  playBonus() {
    this.playTone(860, 0.1, 'triangle', 0.035);
    this.playTone(1200, 0.12, 'square', 0.03, 0.06);
  }

  playTimeBonus() {
    this.playTone(720, 0.08, 'triangle', 0.036);
    this.playTone(1080, 0.1, 'triangle', 0.032, 0.045);
    this.playTone(1460, 0.12, 'square', 0.026, 0.1);
  }

  playLifeBonus() {
    this.playTone(520, 0.09, 'triangle', 0.04);
    this.playTone(760, 0.11, 'sine', 0.035, 0.055);
    this.playTone(1040, 0.14, 'triangle', 0.032, 0.12);
  }

  playScorePanel() {
    [392, 493.88, 587.33, 783.99].forEach((frequency, index) => {
      this.playTone(frequency, 0.16, 'triangle', 0.062, index * 0.075);
      this.playTone(frequency * 2, 0.08, 'sine', 0.022, index * 0.075 + 0.03);
    });
    this.playNoiseBurst(0.15, 0.04, 3200, 'highpass', 0.24);
  }

  playWinJingle() {
    const notes = [523.25, 659.25, 783.99, 1046.5, 1318.51, 1567.98];

    notes.forEach((frequency, index) => {
      this.playTone(frequency, 0.2, index % 2 === 0 ? 'square' : 'triangle', 0.075, index * 0.068);
      this.playTone(frequency * 1.5, 0.12, 'triangle', 0.028, index * 0.068 + 0.025);
    });
    this.playNoiseBurst(0.34, 0.06, 4800, 'highpass', 0.36);
  }

  playLoseJingle() {
    [392, 329.63, 261.63, 196].forEach((frequency, index) => {
      this.playTone(frequency, 0.25, 'triangle', 0.068, index * 0.13);
    });
    this.playToneSweep(420, 180, 0.68, 'sine', 0.058, 0.18);
    this.playNoiseBurst(0.34, 0.038, 760, 'lowpass', 0.24);
  }

  destroy() {
    this.stopAllLoops();
    void this.context?.close();
    this.context = null;
  }

  private playSample(key: AudioSampleKey, options: AudioSampleOptions = {}) {
    if (!this.enabled) {
      return Promise.resolve<HTMLAudioElement | null>(null);
    }

    const { loop = false, playbackRate = 1, restart = true, volume = 1 } = options;
    const audio = loop ? this.getLoopingSample(key) : new Audio(AUDIO_SAMPLE_PATHS[key]);

    audio.preload = 'auto';
    audio.loop = loop;
    audio.playbackRate = playbackRate;
    audio.volume = Math.min(1, Math.max(0, volume));

    if (restart) {
      try {
        audio.currentTime = 0;
      } catch {
        // Some browsers can reject currentTime until metadata is ready.
      }
    }

    if (!loop) {
      this.activeSamples.add(audio);
      audio.addEventListener('ended', () => this.activeSamples.delete(audio), { once: true });
    }

    return audio.play().then(
      () => audio,
      () => {
        if (loop) {
          this.loopingSamples.delete(key);
        } else {
          this.activeSamples.delete(audio);
        }

        return null;
      },
    );
  }

  private getLoopingSample(key: AudioSampleKey) {
    const current = this.loopingSamples.get(key);

    if (current) {
      return current;
    }

    const audio = new Audio(AUDIO_SAMPLE_PATHS[key]);
    this.loopingSamples.set(key, audio);
    return audio;
  }

  private stopLoopingSample(key: AudioSampleKey) {
    const audio = this.loopingSamples.get(key);

    if (!audio) {
      return;
    }

    audio.pause();

    try {
      audio.currentTime = 0;
    } catch {
      // Ignore media that has not loaded metadata yet.
    }

    this.loopingSamples.delete(key);
  }

  private stopLoopingSamples() {
    [...this.loopingSamples.keys()].forEach((key) => this.stopLoopingSample(key));
  }

  private stopActiveSamples() {
    this.activeSamples.forEach((audio) => {
      audio.pause();

      try {
        audio.currentTime = 0;
      } catch {
        // Ignore media that has not loaded metadata yet.
      }
    });
    this.activeSamples.clear();
  }

  private playGameplayStep() {
    if (!this.enabled) {
      return;
    }

    const scale = [392, 440, 493.88, 587.33, 659.25, 587.33, 493.88, 440];
    const bass = [98, 98, 130.81, 130.81, 146.83, 146.83, 130.81, 98];
    const step = this.gameplayStep % 16;

    if (step % 4 === 0) {
      this.playTone(bass[(step / 2) % bass.length], 0.18, 'triangle', 0.022);
    }

    this.playTone(scale[step % scale.length], 0.075, step % 2 === 0 ? 'square' : 'triangle', 0.012);

    if (step % 4 === 2) {
      this.playNoiseBurst(0.045, 0.014, 2300, 'highpass');
    }

    if (step % 8 === 7) {
      this.playTone(scale[(step + 3) % scale.length] * 2, 0.08, 'triangle', 0.014);
    }

    this.gameplayStep += 1;
  }

  private playScoreMusicStep() {
    if (!this.enabled) {
      return;
    }

    const leaderScale = [659.25, 783.99, 987.77, 1174.66, 1318.51, 1174.66, 987.77, 783.99];
    const regularScale = [392, 493.88, 587.33, 783.99, 587.33, 493.88];
    const scale = this.scoreMusicIsLeader ? leaderScale : regularScale;
    const step = this.scoreStep % scale.length;
    const note = scale[step];

    if (step % 4 === 0) {
      this.playTone(this.scoreMusicIsLeader ? 196 : 130.81, 0.26, 'triangle', this.scoreMusicIsLeader ? 0.024 : 0.018);
    }

    this.playTone(note, this.scoreMusicIsLeader ? 0.13 : 0.16, this.scoreMusicIsLeader ? 'square' : 'triangle', this.scoreMusicIsLeader ? 0.028 : 0.02);
    this.playTone(note * 1.5, 0.08, 'sine', this.scoreMusicIsLeader ? 0.012 : 0.008, 0.035);

    if (this.scoreMusicIsLeader && step % 4 === 3) {
      this.playNoiseBurst(0.05, 0.012, 4200, 'highpass', 0.04);
    }

    this.scoreStep += 1;
  }

  private playLossMusicStep() {
    if (!this.enabled) {
      return;
    }

    const scale = [261.63, 246.94, 220, 196, 174.61, 196];
    const step = this.lossStep % scale.length;

    this.playTone(scale[step], 0.34, 'triangle', 0.024);

    if (step % 3 === 0) {
      this.playTone(scale[step] / 2, 0.42, 'sine', 0.018);
      this.playNoiseBurst(0.16, 0.012, 580, 'lowpass', 0.05);
    }

    this.lossStep += 1;
  }

  private playFinalCountdownCue() {
    this.playToneSweep(220, 420, 0.45, 'sawtooth', 0.03);
    this.playNoiseBurst(0.42, 0.025, 900, 'bandpass');
  }

  private playSuspenseStep() {
    if (!this.enabled) {
      return;
    }

    const pulse = this.suspenseStep % 4;
    const frequency = pulse === 0 ? 92 : pulse === 2 ? 116 : 138;

    this.playTone(frequency, 0.24, 'sine', 0.034);
    this.playToneSweep(520 + pulse * 80, 420 + pulse * 54, 0.18, 'triangle', 0.012, 0.04);

    if (pulse === 3) {
      this.playNoiseBurst(0.11, 0.026, 1200, 'bandpass', 0.08);
    }

    this.suspenseStep += 1;
  }

  private playTone(
    frequency: number,
    durationSeconds: number,
    oscillatorType: OscillatorType,
    gainValue: number,
    delaySeconds = 0,
  ) {
    if (!this.enabled) {
      return;
    }

    const audioContext = this.getContext();

    if (!audioContext) {
      return;
    }

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    const startAt = audioContext.currentTime + delaySeconds;

    oscillator.type = oscillatorType;
    oscillator.frequency.value = frequency;
    gainNode.gain.setValueAtTime(gainValue, startAt);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, startAt + durationSeconds);

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.start(startAt);
    oscillator.stop(startAt + durationSeconds);
  }

  private playToneSweep(
    fromFrequency: number,
    toFrequency: number,
    durationSeconds: number,
    oscillatorType: OscillatorType,
    gainValue: number,
    delaySeconds = 0,
  ) {
    if (!this.enabled) {
      return;
    }

    const audioContext = this.getContext();

    if (!audioContext) {
      return;
    }

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    const startAt = audioContext.currentTime + delaySeconds;

    oscillator.type = oscillatorType;
    oscillator.frequency.setValueAtTime(Math.max(1, fromFrequency), startAt);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, toFrequency), startAt + durationSeconds);
    gainNode.gain.setValueAtTime(gainValue, startAt);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, startAt + durationSeconds);

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.start(startAt);
    oscillator.stop(startAt + durationSeconds);
  }

  private playNoiseBurst(
    durationSeconds: number,
    gainValue: number,
    filterFrequency: number,
    filterType: BiquadFilterType,
    delaySeconds = 0,
  ) {
    if (!this.enabled) {
      return;
    }

    const audioContext = this.getContext();

    if (!audioContext) {
      return;
    }

    const sampleCount = Math.max(1, Math.floor(audioContext.sampleRate * durationSeconds));
    const buffer = audioContext.createBuffer(1, sampleCount, audioContext.sampleRate);
    const data = buffer.getChannelData(0);

    for (let index = 0; index < sampleCount; index += 1) {
      const envelope = 1 - index / sampleCount;
      data[index] = (Math.random() * 2 - 1) * envelope;
    }

    const source = audioContext.createBufferSource();
    const filter = audioContext.createBiquadFilter();
    const gainNode = audioContext.createGain();
    const startAt = audioContext.currentTime + delaySeconds;

    filter.type = filterType;
    filter.frequency.value = filterFrequency;
    filter.Q.value = filterType === 'bandpass' ? 8 : 1.2;
    gainNode.gain.setValueAtTime(gainValue, startAt);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, startAt + durationSeconds);

    source.buffer = buffer;
    source.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(audioContext.destination);
    source.start(startAt);
    source.stop(startAt + durationSeconds);
  }

  private getContext() {
    const AudioContextConstructor =
      window.AudioContext ??
      (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

    if (!AudioContextConstructor) {
      return null;
    }

    if (!this.context) {
      this.context = new AudioContextConstructor();
    }

    if (this.context.state === 'suspended') {
      void this.context.resume();
    }

    return this.context;
  }
}
